/**
 * Destino y naturaleza del CTA de cada personaje de guía.
 *
 * Vive aquí (y no en los componentes) porque lo consumen dos bloques distintos
 * del artículo — el preview de llamada y la oferta posterior a imprimir — y
 * tenerlo duplicado era la vía rápida a que uno apuntara a una ruta muerta.
 */

import { getLocalePrefix } from '@/lib/locale-prefix'
import { getCharacterAuthPath, type CharacterAuthVariant } from '@/lib/auth-paths'
import type { GuideCharacterKey, GuideLocale } from './types'

export const LIVE_CALL_GUIDE_CHARACTERS = [
  'tooth-fairy',
  'raton-perez',
  'easter',
  'halloween',
] as const

export type LiveCallGuideCharacterKey = (typeof LIVE_CALL_GUIDE_CHARACTERS)[number]

/** Personajes de temporada: hoy solo se puede ofrecer el aviso de apertura. */
export type SeasonalGuideCharacterKey = Exclude<GuideCharacterKey, LiveCallGuideCharacterKey>

/**
 * Alias de las claves de guía a las variantes canónicas de auth. No contiene
 * rutas: los slugs viven en `lib/auth-paths`; aquí solo se expresa la diferencia
 * de nombre entre `tooth-fairy` y la variante histórica `hada`.
 */
type ClassicGuideCharacterKey = Exclude<GuideCharacterKey, 'easter' | 'halloween'>

const AUTH_VARIANT_BY_GUIDE_CHARACTER = {
  santa: 'santa',
  reyes: 'reyes',
  'tooth-fairy': 'hada',
  'raton-perez': 'raton-perez',
} as const satisfies Record<ClassicGuideCharacterKey, CharacterAuthVariant>

type CatalogGuideCharacterKey = Exclude<GuideCharacterKey, ClassicGuideCharacterKey>

/** Rutas excepcionales de catálogo, incluida su cadena de intención. */
export const GUIDE_CHARACTER_AUTH_PATH = {
  // Reutiliza la cadena de intención de las landings del catálogo: tras el
  // login el dashboard abre resaltando al Conejo.
  easter: '/auth-mundo-magico?from=conejo-de-pascua&character=generic-conejo-pascua',
  // Misma cadena que el Conejo: los cinco monstruos son personajes del
  // catálogo, y Bruma el fantasmita es quien protagoniza el hero de /halloween.
  halloween: '/auth-mundo-magico?from=halloween&character=generic-fantasma',
} as const satisfies Record<CatalogGuideCharacterKey, string>

function isClassicGuideCharacter(
  character: GuideCharacterKey,
): character is ClassicGuideCharacterKey {
  return character in AUTH_VARIANT_BY_GUIDE_CHARACTER
}

/** ¿La llamada de este personaje se puede hacer hoy? */
export function isLiveCallCharacter(
  character: GuideCharacterKey,
): character is LiveCallGuideCharacterKey {
  return (LIVE_CALL_GUIDE_CHARACTERS as readonly string[]).includes(character)
}

/** ¿Solo temporada? Es el complementario de la anterior, no una segunda lista. */
export function isSeasonalCharacter(
  character: GuideCharacterKey,
): character is SeasonalGuideCharacterKey {
  return !isLiveCallCharacter(character)
}

/** Href localizado al flujo de llamada del personaje de una guía. */
export function getGuideCharacterAuthHref(character: GuideCharacterKey, locale: GuideLocale): string {
  const prefix = getLocalePrefix(locale)
  if (!isClassicGuideCharacter(character)) return `${prefix}${GUIDE_CHARACTER_AUTH_PATH[character]}`
  return `${prefix}${getCharacterAuthPath(AUTH_VARIANT_BY_GUIDE_CHARACTER[character])}`
}
